import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { PublicMenuItem } from '../models/menu'
import toast from 'react-hot-toast'
import i18n from '../i18n'

export interface CartItem {
  item: PublicMenuItem
  quantity: number
  businessSlug: string // Store the business slug to separate carts per business
}

interface CartState {
  items: CartItem[]
  isOpen: boolean
  currentBusinessSlug: string | null // Track current business

  // Actions
  addItem: (item: PublicMenuItem, quantity?: number, businessSlug?: string) => void
  removeItem: (itemId: number, businessSlug?: string) => void
  updateQuantity: (itemId: number, quantity: number, businessSlug?: string) => void
  clearCart: (businessSlug?: string) => void
  setCurrentBusiness: (businessSlug: string) => void
  openCart: () => void
  closeCart: () => void
  toggleCart: () => void

  // Computed
  getItemsForBusiness: (businessSlug: string) => CartItem[]
  getTotalItems: (businessSlug?: string) => number
  getTotalPrice: (businessSlug?: string) => number
  getFormattedTotalPrice: (businessSlug?: string) => string
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      currentBusinessSlug: null,

      addItem: (item: PublicMenuItem, quantity: number = 1, businessSlug?: string) => {
        if (!item.isAvailable) {
          toast.error(i18n.t('itemNotAvailable'))
          return
        }

        // Use provided businessSlug or current business
        const targetBusinessSlug = businessSlug || get().currentBusinessSlug
        if (!targetBusinessSlug) {
          console.warn('No business slug provided for cart item')
          return
        }

        set((state) => {
          // Filter items for this business only
          const businessItems = state.items.filter(cartItem => cartItem.businessSlug === targetBusinessSlug)
          
          // Create a unique key for cart items (itemId)
          const itemKey = `${item.id}`
          
          const existingItem = businessItems.find((cartItem) => {
            const cartItemKey = `${cartItem.item.id}`
            return cartItemKey === itemKey
          })

          if (existingItem) {
            // Update quantity if item already exists
            const updatedItems = state.items.map((cartItem) => {
              // Only update if it's from the same business
              if (cartItem.businessSlug !== targetBusinessSlug) return cartItem
              
              const cartItemKey = `${cartItem.item.id}`
              return cartItemKey === itemKey
                ? { ...cartItem, quantity: cartItem.quantity + quantity }
                : cartItem
            })
            toast.success(i18n.t('itemQuantityUpdated'))
            return { items: updatedItems }
          } else {
            // Add new item with business slug
            toast.success(i18n.t('itemAddedToCart'))
            return { items: [...state.items, { item, quantity, businessSlug: targetBusinessSlug }] }
          }
        })
      },

      removeItem: (itemId: number, businessSlug?: string) => {
        const targetBusinessSlug = businessSlug || get().currentBusinessSlug
        set((state) => ({
          items: state.items.filter((cartItem) => {
            // Only remove items from the specified business
            if (targetBusinessSlug && cartItem.businessSlug !== targetBusinessSlug) {
              return true
            }
            return cartItem.item.id !== itemId
          })
        }))
        toast.success(i18n.t('itemRemovedFromCart'))
      },

      updateQuantity: (itemId: number, quantity: number, businessSlug?: string) => {
        if (quantity <= 0) {
          get().removeItem(itemId, businessSlug)
          return
        }

        const targetBusinessSlug = businessSlug || get().currentBusinessSlug
        set((state) => ({
          items: state.items.map((cartItem) => {
            // Only update items from the specified business
            if (targetBusinessSlug && cartItem.businessSlug !== targetBusinessSlug) {
              return cartItem
            }
            return cartItem.item.id === itemId
              ? { ...cartItem, quantity }
              : cartItem
          })
        }))
      },

      clearCart: (businessSlug?: string) => {
        const targetBusinessSlug = businessSlug || get().currentBusinessSlug
        if (targetBusinessSlug) {
          // Clear only items from this business
          set((state) => ({
            items: state.items.filter(cartItem => cartItem.businessSlug !== targetBusinessSlug)
          }))
        } else {
          // Clear all items if no business specified
          set({ items: [] })
        }
        toast.success(i18n.t('cartCleared'))
      },

      setCurrentBusiness: (businessSlug: string) => {
        set({ currentBusinessSlug: businessSlug })
      },

      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),

      getItemsForBusiness: (businessSlug: string) => {
        return get().items.filter(cartItem => cartItem.businessSlug === businessSlug)
      },

      getTotalItems: (businessSlug?: string) => {
        const targetBusinessSlug = businessSlug || get().currentBusinessSlug
        const items = targetBusinessSlug 
          ? get().getItemsForBusiness(targetBusinessSlug)
          : get().items
        return items.reduce((total, cartItem) => total + cartItem.quantity, 0)
      },

      getTotalPrice: (businessSlug?: string) => {
        const targetBusinessSlug = businessSlug || get().currentBusinessSlug
        const items = targetBusinessSlug 
          ? get().getItemsForBusiness(targetBusinessSlug)
          : get().items
        return items.reduce((total, cartItem) => {
          return total + cartItem.item.price * cartItem.quantity
        }, 0)
      },

      getFormattedTotalPrice: (businessSlug?: string) => {
        const total = get().getTotalPrice(businessSlug)
        // Format as SAR (Saudi Riyal) - adjust based on your needs
        return `${total.toFixed(2)} `
      }
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({
        items: state.items,
        // Don't persist isOpen state
      }),
    }
  )
)

